import { useAuth } from '@/context/AuthContext';
import useDowntimeFlag from '@/hooks/useDowntimeFlag';
import useLimits from '@/hooks/useLimits';
import {
  addUploadingFilesAtom,
  updateUploadingFileAtom,
} from '@/store/fileUploadStore';
import { useSetAtom } from 'jotai';
import { UploadIcon } from 'lucide-react';
import { motion } from 'motion/react';
import { useRouter } from 'next/router';
import type React from 'react';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  FileUploadToastContent,
  UPLOAD_TOAST_ID,
} from '../Ingest/FileUploadToastContent';
import useIngestCustomAudio from '../Ingest/useIngestCustomAudio';
import useIngestCustomVideo from '../Ingest/useIngestCustomVideo';
import useIngestFile from '../Ingest/useIngestFile';

interface FileSystemEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
  filesystem: FileSystem;
}

interface FileSystem {
  name: string;
  root: FileSystemDirectoryEntry;
}

interface FileSystemFileEntry extends FileSystemEntry {
  file(
    successCallback: (file: File) => void,
    errorCallback?: (error: Error) => void,
  ): void;
}

interface FileSystemDirectoryEntry extends FileSystemEntry {
  createReader(): FileSystemDirectoryReader;
}

interface FileSystemDirectoryReader {
  readEntries(
    successCallback: (entries: FileSystemEntry[]) => void,
    errorCallback?: (error: Error) => void,
  ): void;
}

const SiteDropzone = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useAuth();
  const { ingestFile } = useIngestFile();
  const { ingestCustomVideo } = useIngestCustomVideo();
  const { ingestCustomAudio } = useIngestCustomAudio();
  const { exceededLimits, limits, usage } = useLimits();
  const [isDragActive, setIsDragActive] = useState(false);
  const { isDowntime } = useDowntimeFlag();
  const _router = useRouter();
  const addFilesToUpload = useSetAtom(addUploadingFilesAtom);
  const updateFileStatus = useSetAtom(updateUploadingFileAtom);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  };

  const allowedTypes = [
    'application/pdf',
    'text/markdown',
    'text/plain',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/jpg',
    'image/tiff',
    'image/bmp',
    'image/heic',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
    'application/vnd.ms-powerpoint', // ppt
    'application/msword', // doc
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
    'video/mp4',
    'audio/mpeg', // mp3
    'audio/wav', // wav
    'audio/x-wav', // wav alternative MIME type
    'application/epub+zip', // epub
  ];

  const isValidFile = (file: File): boolean => {
    if (allowedTypes.includes(file.type)) {
      return true;
    }

    const extension = `.${file.name.split('.').pop()?.toLowerCase()}`;
    const allowedExtensions = [
      '.pdf',
      '.md',
      '.txt',
      '.jpeg',
      '.jpg',
      '.png',
      '.gif',
      '.tiff',
      '.bmp',
      '.heic',
      '.pptx',
      '.ppt',
      '.doc',
      '.docx',
      '.mp4',
      '.mp3',
      '.wav',
      '.epub',
    ];

    return allowedExtensions.includes(extension);
  };

  const processEntries = async (dataTransferItems: DataTransferItemList) => {
    const validFiles: File[] = [];
    const allPromises: Promise<void>[] = [];

    for (let i = 0; i < dataTransferItems.length; i++) {
      const item = dataTransferItems[i];
      // Try to get the entry using the WebkitGetAsEntry API
      const entry = item.webkitGetAsEntry?.();

      if (entry?.isDirectory) {
        // Process directory
        allPromises.push(
          processDirectoryEntry(
            entry as unknown as FileSystemDirectoryEntry,
            validFiles,
          ),
        );
      } else if (entry?.isFile) {
        // Process single file
        allPromises.push(
          new Promise<void>((resolve) => {
            (entry as unknown as FileSystemFileEntry).file?.(
              (file) => {
                if (isValidFile(file)) {
                  validFiles.push(file);
                  console.log('Added file:', file.name, file.type);
                }
                resolve();
              },
              () => resolve(),
            );
          }),
        );
      }
    }

    await Promise.all(allPromises);
    return validFiles;
  };

  const processDirectoryEntry = async (
    directoryEntry: FileSystemDirectoryEntry,
    validFiles: File[],
  ): Promise<void> => {
    // Create a promise to process this directory
    return new Promise<void>((resolveDirectory) => {
      const directoryReader = directoryEntry.createReader();

      // Function to read all entries in batches
      const readAllEntries = (allEntries: FileSystemEntry[] = []) => {
        directoryReader.readEntries(
          async (entries) => {
            // When no more entries to read
            if (entries.length === 0) {
              try {
                // Process all collected entries
                const entryPromises: Promise<void>[] = [];

                for (const entry of allEntries) {
                  if (entry.isFile) {
                    // Process file
                    entryPromises.push(
                      new Promise<void>((resolveFile) => {
                        (entry as FileSystemFileEntry).file?.(
                          (file) => {
                            if (isValidFile(file)) {
                              validFiles.push(file);
                              console.log(
                                'Added file from directory:',
                                file.name,
                                file.type,
                              );
                            }
                            resolveFile();
                          },
                          () => resolveFile(),
                        );
                      }),
                    );
                  } else if (entry.isDirectory) {
                    // Recursively process subdirectory
                    entryPromises.push(
                      processDirectoryEntry(
                        entry as FileSystemDirectoryEntry,
                        validFiles,
                      ),
                    );
                  }
                }

                // Wait for all entries to be processed
                await Promise.all(entryPromises);
                resolveDirectory();
              } catch (error) {
                console.error('Error processing directory entries:', error);
                resolveDirectory();
              }
            } else {
              // Collect entries and continue reading
              allEntries.push(...entries);
              readAllEntries(allEntries);
            }
          },
          (error) => {
            console.error('Error reading directory entries:', error);
            resolveDirectory();
          },
        );
      };

      // Start reading entries
      readAllEntries();
    });
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (isDowntime) {
      toast.error("Import unavailable. We'll be back online soon!");
      setIsDragActive(false);
      return;
    }
    if (!user) {
      setIsDragActive(false);
      return;
    }

    setIsDragActive(false);

    let validFiles: File[] = [];

    // Check if we have items API available (needed for folder support)
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      try {
        validFiles = await processEntries(e.dataTransfer.items);
      } catch (_) {
        // Fallback to simple file processing
        validFiles = Array.from(e.dataTransfer.files).filter((file) =>
          isValidFile(file),
        );
      }
    } else {
      // Fallback to simple file processing
      validFiles = Array.from(e.dataTransfer.files).filter((file) =>
        isValidFile(file),
      );
    }

    if (validFiles.length === 0) {
      console.info('No valid files found');
      return;
    }

    if (
      limits?.legacyImportLimit &&
      usage &&
      !exceededLimits?.isImportLimitExceeded
    ) {
      const remainingImportLimit =
        limits.legacyImportLimit - usage.legacyImportCount;

      if (validFiles.length > remainingImportLimit) {
        validFiles = validFiles.slice(0, remainingImportLimit);
        toast.info(
          `We will only process ${remainingImportLimit} files because of import limits.`,
        );
      }
    }

    // Add files to the global state with initial 'uploading' status
    const filesToAdd = validFiles.map((file) => ({ name: file.name }));
    const addedFilesState = addFilesToUpload(filesToAdd);

    // Show the custom toast
    toast.custom((t) => <FileUploadToastContent toastId={t} />, {
      id: UPLOAD_TOAST_ID,
      duration: Number.POSITIVE_INFINITY,
      dismissible: false,
      position: 'bottom-left',
      className: '!px-0 !py-0 bg-popover border-border !rounded-xl',
    });

    const filesPromises = validFiles.map(async (file, index) => {
      const fileState = addedFilesState[index];
      if (!fileState) {
        console.error('Mismatch between input files and state files');
        return null;
      }

      try {
        let chatbotId: string | null | undefined = null;

        if (file.type === 'video/mp4') {
          chatbotId = await ingestCustomVideo([file], false);
        } else if (file.type.startsWith('audio/')) {
          chatbotId = await ingestCustomAudio([file], false);
        } else {
          chatbotId = await ingestFile(file);
        }

        if (chatbotId) {
          updateFileStatus({
            id: fileState.id,
            status: 'IN_PROGRESS',
            chatbotId: chatbotId,
          });
          return chatbotId;
        }

        updateFileStatus({
          id: fileState.id,
          status: 'FAILED',
          errorMessage: 'File could not be processed.',
        });
        return null;
      } catch (error: unknown) {
        console.error('Error ingesting file:', file.name, error);
        const errorMessage =
          error instanceof Error ? error.message : 'Upload failed';
        updateFileStatus({
          id: fileState.id,
          status: 'FAILED',
          errorMessage: errorMessage,
        });
        return null;
      }
    });

    // Navigate to the first successful upload
    Promise.race(filesPromises.map((p) => p.catch(() => null))).catch(
      () => null,
    );
  };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  return (
    <div
      className={`relative w-full h-full ${isDragActive ? 'bg-opacity-50' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragActive && (
        <motion.div
          className="absolute pointer-events-none inset-0 backdrop-blur-sm bg-muted/60 z-50 flex flex-col justify-center items-center gap-1"
          initial="hidden"
          animate="visible"
          exit="hidden"
          variants={overlayVariants}
          transition={{ duration: 0.1 }}
        >
          <UploadIcon className="text-muted-foreground2" size={36} />

          <div className="flex flex-col gap-1 items-center">
            <h2 className="text-foreground text-xl">Upload files</h2>
            <p className="text-foreground text-base">Drop here</p>
          </div>
        </motion.div>
      )}

      {children}
    </div>
  );
};

export default SiteDropzone;
